"use client"
import { create } from "zustand"
import { PreceptorProfile } from "@/types/preceptor"
import {
  getMyPreceptorProfiles,
  requestAdditionalPreceptorProfile,
  setActivePreceptorProfile,
  setDefaultPreceptorProfile,
  updateMe,
  type PreceptorMultiProfileAccess,
} from "@/services/preceptor-service"

export interface PreceptorStoreState {
  profiles: PreceptorProfile[]
  activeProfileId: number | null
  defaultProfileId: number | null
  loading: boolean
  saving: boolean
  requestingAdditionalProfile: boolean
  error: string | null
  lastSavedAt: number | null
  loaded: boolean
  multiProfile: PreceptorMultiProfileAccess

  // Actions
  fetchProfile: () => Promise<void>
  markDefault: (profileId: number) => Promise<void>
  setActive: (profileId: number) => Promise<void>
  updateProfile: (profile: PreceptorProfile) => Promise<void>
  setProfile: (profile: PreceptorProfile) => void
  requestAdditionalProfile: (reason: string) => Promise<void>
  clearError: () => void
  reset: () => void
  getTrack: (track: string) => string
}

const initialState = {
  profiles: [] as PreceptorProfile[],
  activeProfileId: null as number | null,
  defaultProfileId: null as number | null,
  loading: false,
  saving: false,
  requestingAdditionalProfile: false,
  error: null,
  lastSavedAt: null,
  loaded: false,
  multiProfile: {
    can_create: false,
    can_request: false,
    pending_request: null,
    latest_request: null,
    enabled_at: null,

  } as PreceptorMultiProfileAccess,
}

export const usePreceptorStore = create<PreceptorStoreState>()((set, get) => ({
  ...initialState,

  fetchProfile: async () => {
    set({ loading: true, error: null })
    try {
      const response = await getMyPreceptorProfiles()
      set({
        profiles: response.profiles ?? [],
        activeProfileId: response.active_preceptor_id ?? null,
        defaultProfileId: response.default_preceptor_id ?? null,
        loading: false,
        loaded: true,
        multiProfile: {
          ...initialState.multiProfile,
          ...(response.multi_profile ?? {}),
        },
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to fetch profile"
      set({ error: errorMessage, loading: false, loaded: true })
    }
  },

  markDefault: async (profileId: number) => {
    set({ saving: true, error: null })
    try {
      const updated = await setDefaultPreceptorProfile(profileId)
      const profiles = get().profiles.map(profile =>
        profile.id === updated.id
          ? { ...profile, ...updated, is_default: true }
          : { ...profile, is_default: false }
      )
      set({
        profiles,
        defaultProfileId: updated.id ?? null,
        activeProfileId: updated.id ?? get().activeProfileId,
        saving: false,
        lastSavedAt: Date.now(),
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update default profile"
      set({ error: errorMessage, saving: false })
      throw error
    }
  },

  setActive: async (profileId: number) => {
    set({ saving: true, error: null })
    try {
      const response = await setActivePreceptorProfile(profileId)

      set(state => {
        const updatedProfiles = state.profiles.map(profile => {
          if (profile.id === response.active_profile.id) {
            return { ...profile, ...response.active_profile }
          }

          if (response.previous_profile && profile.id === response.previous_profile.id) {
            return { ...profile, last_active: response.previous_profile.last_active }
          }

          return profile
        })

        return {
          profiles: updatedProfiles,
          activeProfileId: response.active_preceptor_id,
          defaultProfileId: response.default_preceptor_id ?? state.defaultProfileId,
          saving: false,
          lastSavedAt: Date.now(),
          error: null,
          multiProfile: state.multiProfile,
        }
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to activate profile"
      set({ error: errorMessage, saving: false })
      throw error
    }
  },

  updateProfile: async (profile: PreceptorProfile) => {
    set({ saving: true, error: null })
    try {
      const updated = await updateMe(profile)
      const profiles = get().profiles.map(item =>
        item.id === updated.id ? { ...item, ...updated } : item
      )
      set({ profiles, saving: false, lastSavedAt: Date.now() })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update profile"
      set({ error: errorMessage, saving: false })
      throw error
    }
  },

  setProfile: (profile: PreceptorProfile) => {
    const profiles = get().profiles
    if (profile.id) {
      const index = profiles.findIndex(item => item.id === profile.id)
      if (index >= 0) {
        const updatedProfiles = [...profiles]
        updatedProfiles[index] = { ...updatedProfiles[index], ...profile }
        set({ profiles: updatedProfiles })
        return
      }
    }

    set({ profiles: [...profiles, profile] })
  },

  requestAdditionalProfile: async (reason: string) => {
    set({ requestingAdditionalProfile: true, error: null })
    try {
      const request = await requestAdditionalPreceptorProfile(reason)
      set(state => ({
        requestingAdditionalProfile: false,
        multiProfile: {
          can_create: state.multiProfile.can_create,
          can_request: false,
          pending_request: request,
          latest_request: request,
          enabled_at: state.multiProfile.enabled_at ?? null,
        },
      }))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to submit request"
      set({ error: errorMessage, requestingAdditionalProfile: false })
      throw error
    }
  },

  clearError: () => {
    set({ error: null })
  },

  reset: () => {
    set({ ...initialState })
  },
  getTrack: (track: string) => {
    switch (track) {
      case 'np':
        return 'Nurse Practitioner';
      case 'pa':
        return 'Physician Assistant';
      case 'img':
        return 'International Medical Graduate';
      default:
        return 'Unknown Track';
    }
  }
}))
